import type { CrashReporter, RemoteConfigService, Unsubscribe } from '@mendikot/app-core';
import {
  DEFAULT_REMOTE_SETTINGS,
  parseRemoteSettings,
  RC_DEFAULTS,
  type RemoteSettings,
} from '@mendikot/protocol';
import type { FirebaseApp } from 'firebase/app';

/**
 * Remote Config (plan §6): cached values activate at boot without blocking, a background fetch
 * follows, and real-time updates stream in so festival themes switch from the console without a deploy.
 */
export class WebRemoteConfig implements RemoteConfigService {
  private settings: RemoteSettings = DEFAULT_REMOTE_SETTINGS;
  private readonly listeners = new Set<(s: RemoteSettings) => void>();
  private refreshImpl: (() => Promise<void>) | null = null;

  constructor(
    private readonly app: FirebaseApp | null,
    private readonly crash: CrashReporter,
  ) {}

  async start(): Promise<void> {
    if (!this.app) return;
    try {
      const rc = await import('firebase/remote-config');
      if (!(await rc.isSupported())) return;
      const remoteConfig = rc.getRemoteConfig(this.app);
      remoteConfig.settings.minimumFetchIntervalMillis = import.meta.env.DEV ? 0 : 60 * 60 * 1000;
      remoteConfig.defaultConfig = RC_DEFAULTS;
      const read = () => {
        this.settings = parseRemoteSettings((key) => rc.getString(remoteConfig, key));
        for (const listener of this.listeners) listener(this.settings);
        this.crash.breadcrumb('remote-config', `activated theme=${this.settings.themeId}`);
      };
      await rc.ensureInitialized(remoteConfig);
      read();
      this.refreshImpl = async () => {
        await rc.fetchAndActivate(remoteConfig);
        read();
      };
      void this.refreshImpl().catch((error) =>
        this.crash.captureError(error, { where: 'rc_fetch' }),
      );
      rc.onConfigUpdate(remoteConfig, {
        next: () => void rc.activate(remoteConfig).then(read),
        error: (error) => this.crash.breadcrumb('remote-config', `realtime error ${String(error)}`),
        complete: () => undefined,
      });
    } catch (error) {
      this.crash.captureError(error, { where: 'rc_start' });
    }
  }

  current(): RemoteSettings {
    return this.settings;
  }

  onChange(listener: (s: RemoteSettings) => void): Unsubscribe {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async refresh(): Promise<void> {
    await this.refreshImpl?.().catch(() => undefined);
  }
}
